import * as React from "react";
import * as moment from "moment";
import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import pageStyles from "./styles/ReceiveViewStyles";
import Model from "hooks/Model";
import { useChannel, useWallet } from "services";
import { returnFormatNumber } from "utils/validations";
import PlaceholderWin from "assets/images/avatar_win.png";
import { databaseService } from "utils/firebase";
import { useNavigate } from "react-router-dom";
import { RoutesPaths } from "pages/routes";
import ReactPlayer from "react-player";
import { DivScrollHorizontal } from "components";
import { CouponProps, MyCouponProps } from "models";
import Api from "utils/api";
import { Store } from "hooks/main_store";

function RedeemInitialView() {
  const { state } = useContext(Store);
  const styles = pageStyles();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { currentBalance, refetch } = useWallet();
  const { recommendedVideos } = useChannel({
    channel: "froin",
  });

  const showWaiting = useCallback(
    (id: string) => {
      const lastDatePlayed = state.rankingHistory?.[id];
      const countSeconds = moment().diff(
        moment(lastDatePlayed, "x"),
        "seconds",
      );

      return lastDatePlayed && countSeconds < 86400;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [state.rankingHistory],
  );
  const topVideos = React.useMemo(
    () =>
      recommendedVideos
        .map((value) => ({
          ...value,
          sort: Math.random(),
          isWaiting: showWaiting(value.id),
        }))
        .sort((a, b) => a.sort - b.sort)
        .sort((a, b) => (a?.isWaiting ? 1 : 0) - (b?.isWaiting ? 1 : 0))
        .slice(0, 3),
    [recommendedVideos, showWaiting],
  );

  const [allCoupons, setAllCoupons] = useState<CouponProps[]>([]);
  const [couponsToRender, setCouponsToRender] = useState<CouponProps[]>([]);
  const [allMyCoupons, setAllMyCoupons] = useState<MyCouponProps[]>([]);

  const navigateToVideos = () => {
    navigate(RoutesPaths.profileVideos);
  };

  const navigateToRegisterCommerce = () => {
    navigate(`${RoutesPaths.home}?page=register-commerce`);
  };

  const navigateToReward = (id: string) => {
    navigate(`/detail-video?id=${id}`);
  };

  const renderRecommendedVideos = () =>
    topVideos.map((video) => (
      <div key={video.id} className={styles.rowVideoRecommended}>
        <div className={styles.imgVideoRecommended}>
          {video?.imageUrl || !video?.url ? (
            <img src={video.imageUrl || PlaceholderWin} alt="Artista" />
          ) : (
            <ReactPlayer
              url={video?.url}
              light
              width="100%"
              height="100%"
              playing={false}
              playIcon={<></>}
            />
          )}
        </div>
        <div className={styles.infoVideoRecommended}>
          <p className={styles.nameVideoRecommended}>{video.name}</p>
          <p className={styles.prizeVideoRecommended}>
            {returnFormatNumber(video.limit)} puntos
          </p>
        </div>
        <button
          className={styles.buttonVideoRecommended}
          onClick={() => navigateToReward(video.id)}
        >
          Ganar
        </button>
      </div>
    ));

  const buyCoupon = async (coupon: CouponProps) => {
    try {
      Model.setData("loading", true);
      const response = await Api.post("/redeem/buyCoupon", {
        couponId: coupon.id,
      });

      if (response.ok && response.data === "done") {
        Model.updateAlerts({
          message:
            "¡Felicidades! Ya tienes tu bono, lo puedes redimir en el comercio",
          variant: "success",
        });
        await refetch();
        await getCoupons();
      } else {
        throw new Error(response.data as string);
      }
    } catch (error) {
      Model.updateAlerts({
        message: error?.message || error,
        variant: "error",
      });
    } finally {
      Model.setData("loading", false);
    }
  };

  const showTermsDialog = (coupon: CouponProps, beforeData: any) => {
    Model.setData("dialog", {
      open: true,
      title: "Instrucciones, Términos y Condiciones",
      text: coupon?.terms?.replace(/\\n/g, "\n"),
      txtRight: "De acuerdo",
      fnRight: () => {
        Model.setData("dialog", {
          ...beforeData,
          [beforeData.fnAux ? "fnAux" : "fnLeft"]: () => {
            showTermsDialog(coupon, beforeData);
          },
        });
      },
    });
  };

  const redeemCoupon = (coupon: CouponProps, isDisabled: boolean) => {
    if (isDisabled) {
      Model.updateAlerts({
        message:
          "Aún no logras completar la cantidad de $FROIN para adquirir este producto",
        variant: "error",
      });
    } else {
      const dataOfDialog = {
        open: true,
        title: "Antes de continuar ...",
        text: `¿Estas de acuerdo en redimir este bono de "${
          coupon?.name
        }"? Se te descontarán ${returnFormatNumber(
          coupon?.price,
        )} $FROIN de tu saldo.\n\nRecuerda que esta acción no se puede revertir.`,
        txtAux: "Instrucciones y T&C",
        fnAux: () => {},
        txtLeft: "Cancelar",
        fnLeft: () => {
          Model.setData("dialog", { open: false });
        },
        txtRight: "De acuerdo",
        fnRight: () => {
          Model.setData("dialog", { open: false });
          buyCoupon(coupon);
        },
      };
      Model.setData("dialog", {
        ...dataOfDialog,
        fnAux: () => {
          showTermsDialog(coupon, dataOfDialog);
        },
      });
    }
  };

  const viewCouponDetail = (
    myCouponInfo: MyCouponProps,
    couponInfo: CouponProps,
  ) => {
    switch (myCouponInfo.status) {
      case "pending":
        Model.setData("dialog", {
          open: true,
          title: "Aún no tenemos información ...",
          text: "Tu orden se encuentra en estado PENDIENTE, lo que significa que aún no hemos generado el código de tu redención para que lo puedas usar en el comercio.\n\nEste proceso se debe completar en las próximas 24 horas después de solicitado.\n\nCualquier solicitud nos puedes escribir a hello@froin.co",
          txtRight: t("commons.confirm"),
          fnRight: () => {
            Model.setData("dialog", { open: false });
          },
        });
        break;
      case "readyToUse":
        const dataOfDialog = {
          open: true,
          title: "Información de tu redención",
          text: `¡Enhorabuena! Ya puedes utilizar tu redención en el comercio ${
            myCouponInfo?.url
              ? "ingresando en el siguiente link para visualizar tu recompensa:\n\n[[Abre tu cupón aquí]]"
              : `presentando el siguiente código:\n\n${myCouponInfo?.code}`
          }\n\nCualquier solicitud nos puedes escribir a hello@froin.co`,
          link: myCouponInfo?.url,
          txtRight: "OK",
          fnRight: () => {
            Model.setData("dialog", { open: false });
          },
          txtLeft: "Instrucciones y T&C",
          fnLeft: () => {},
        };
        Model.setData("dialog", {
          ...dataOfDialog,
          fnLeft: () => {
            showTermsDialog(couponInfo, dataOfDialog);
          },
        });
        break;
      case "redeemed":
        Model.updateAlerts({
          message: "Ya has redimido esta recompensa :)",
          variant: "warning",
        });
        break;
      default:
        Model.updateAlerts({
          message:
            "No reconocemos el estado de tu redención, ponte en contacto con nosotros.",
          variant: "error",
        });
    }
  };

  const renderMyCoupons = () => {
    return allMyCoupons.map((coupon) => {
      const couponInfo = allCoupons.find((data) => data.id === coupon.couponId);
      return (
        <div className={styles.cardCoupon} key={coupon.id}>
          <img
            className={styles.imgCardCoupon}
            alt="Partner"
            src={couponInfo?.image || PlaceholderWin}
          />
          <div className={styles.infoCardCoupon}>
            <p className={styles.labelCardCoupon}>{couponInfo?.name}</p>

            <p className={styles.priceCardCoupon}>
              {t(`commons.${coupon.status}`)}
            </p>
            <p className={styles.priceCardCoupon}>
              {moment(coupon.created, "x").format("DD MMM YYYY, hh:mm a")}
            </p>
            <button
              className={styles.buttonCardCoupon}
              onClick={() => viewCouponDetail(coupon, couponInfo)}
            >
              Ver detalle
            </button>
          </div>
        </div>
      );
    });
  };

  const renderCoupons = () => {
    const returnProgress = (amount) => {
      return (currentBalance / parseInt(amount)) * 100;
    };

    return couponsToRender.map((coupon) => {
      const progress = returnProgress(coupon?.price);
      return (
        <div className={styles.cardCoupon} key={coupon.id}>
          <img
            className={styles.imgCardCoupon}
            alt="Partner"
            src={coupon?.image || PlaceholderWin}
          />
          <div className={styles.infoCardCoupon}>
            <p className={styles.labelCardCoupon}>{coupon?.name}</p>
            <div className={styles.containerProgressCardCoupon}>
              <div
                className={styles.progressCardCoupon}
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className={styles.priceCardCoupon}>
              {returnFormatNumber(coupon?.price)} $FROIN
            </p>
            <button
              className={[
                styles.buttonCardCoupon,
                progress < 100 ? styles.disabledButtonCardCoupon : "",
              ].join(" ")}
              onClick={() => redeemCoupon(coupon, progress < 100)}
            >
              Lo quiero
            </button>
          </div>
        </div>
      );
    });
  };

  const getCoupons = async () => {
    try {
      Model.setData("loading", true);
      let coupons = [];
      const couponsSnap = await databaseService.getDatabase("coupons");

      couponsSnap.forEach((snap) => {
        coupons.push({
          ...(snap.val() || []),
          id: snap.key,
        });
      });

      setAllCoupons(coupons);

      setCouponsToRender(
        coupons
          .filter((item) => !item?.hide)
          .sort((a, b) => (b?.order || 0) - (a?.order || 0)),
      );

      if (state.infoUser?.uid) {
        let myCoupons = [];
        const myCouponsSnap = await databaseService.getDatabase(
          `usersCoupons/${state.infoUser?.uid}`,
        );
        myCouponsSnap.forEach((snap) => {
          myCoupons.push({
            ...(snap.val() || []),
            id: snap.key,
          });
        });
        setAllMyCoupons(myCoupons.sort((a, b) => b.created - a.created));
      }
    } catch (error) {
      Model.updateAlerts({
        message: error?.message || error,
        variant: "error",
      });
    } finally {
      Model.setData("loading", false);
    }
  };

  useEffect(() => {
    getCoupons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!state.loading) {
      Model.setData("stepTutorial", "redeem");
    }
  }, [state.loading]);

  return (
    <>
      <h2 className={styles.title}>Redimir 🎁</h2>

      <div className={styles.rowStats}>
        <div className={styles.colBalance} id="redeem-balance-section">
          <h3 className={styles.titleBalance}>Mi saldo</h3>
          <div className={styles.infoBalance}>
            <p className={styles.labelCurrenct}>$FROIN</p>
            <p className={styles.balance}>
              {returnFormatNumber(currentBalance, 0)}
            </p>
            <p className={styles.labelBalance}>Tu Balance</p>
            <p className={styles.descriptionBalance}>
              Gana apoyando artistas, redime por bonos de descuento, y disfruta
              de las experiencias que compartimos en FROIN
            </p>
          </div>
        </div>
        <div className={styles.colRecommended} id="redeem-recommended-section">
          <h3 className={styles.labelRecommended}>
            Gana puntaje en el Ranking
          </h3>
          <div className={styles.listRecommended}>
            {renderRecommendedVideos()}
            <button
              className={styles.exploreVideoRecommended}
              onClick={navigateToVideos}
            >
              Explorar más contenido
            </button>
          </div>
        </div>
      </div>

      {allMyCoupons.length > 0 && (
        <DivScrollHorizontal label="Tus redenciones">
          <>{renderMyCoupons()}</>
        </DivScrollHorizontal>
      )}

      <DivScrollHorizontal label="Usa tus $FROIN" id="redeem-coupons-section">
        <>
          {renderCoupons()}
          <div className={[styles.cardCoupon, styles.soonCardCoupon].join(" ")}>
            <div className={styles.containerSoonCardCoupon}>
              <p>
                {"👀"} Próximamente anunciaremos más comercios {"🚀"}
              </p>

              <button onClick={navigateToRegisterCommerce}>
                Postular mi comercio
              </button>
            </div>
          </div>
        </>
      </DivScrollHorizontal>
    </>
  );
}

export default RedeemInitialView;
